import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { buyCompanion, buyRelic, equipCompanion } from 'store/relicVault';

// Timings from the designer's handoff (hooks/useStorePurchaseFlow.ts).
const CONFIRM_TO_SUCCESS_MS = 650; // relic icon flies into the vault
const SUCCESS_TO_CLOSE_MS = 1400;
const CHEST_PULSE_MS = 750;

/**
 * The store's confirm -> buy -> success -> auto-close purchase modal, plus the
 * vault-chest pulse after a relic lands in the vault.
 *
 * Unlike the handoff (whose mock purchase could not fail), a failed purchase
 * returns to the confirm stage and shows the error instead of "success".
 */
export function useStorePurchaseFlow({ userId, balance, onServerBalanceChanged }) {
  const dispatch = useDispatch();
  const [activeItem, setActiveItem] = useState(null); // { kind: 'relic'|'companion', id }
  const [stage, setStage] = useState(null); // 'confirm' | 'success' | null
  const [animating, setAnimating] = useState(false);
  const [vaultPulse, setVaultPulse] = useState(false);
  const [error, setError] = useState(null);

  const timers = useRef([]);
  const activeRef = useRef(null);
  const balanceRef = useRef(balance);
  balanceRef.current = balance;

  const later = (fn, ms) => {
    const t = setTimeout(fn, ms);
    timers.current.push(t);
  };

  useEffect(
    () => () => {
      timers.current.forEach(t => clearTimeout(t));
    },
    [],
  );

  const open = useCallback((kind, id) => {
    const item = { kind, id };
    activeRef.current = item;
    setError(null);
    setAnimating(false);
    setActiveItem(item);
    setStage('confirm');
  }, []);

  const openRelic = useCallback(id => open('relic', id), [open]);
  const openCompanion = useCallback(guildId => open('companion', guildId), [open]);

  const close = useCallback(() => {
    activeRef.current = null;
    setActiveItem(null);
    setStage(null);
    setAnimating(false);
    setError(null);
  }, []);

  const confirm = useCallback(() => {
    const active = activeRef.current;
    if (!active || animating) return;
    setError(null);
    setAnimating(true);

    later(async () => {
      const action =
        active.kind === 'relic'
          ? buyRelic({ userId, relicId: active.id, balance: balanceRef.current })
          : buyCompanion({ userId, guildId: active.id, balance: balanceRef.current });
      const result = await dispatch(action);

      // The modal was closed or replaced while the purchase was in flight.
      if (activeRef.current !== active) return;

      setAnimating(false);
      if (!result?.success) {
        setError(result?.error || 'Purchase failed.');
        return;
      }
      if (result.serverBalanceChanged && onServerBalanceChanged) {
        onServerBalanceChanged();
      }
      setStage('success');

      later(() => {
        if (activeRef.current !== active) return;
        activeRef.current = null;
        setActiveItem(null);
        setStage(null);
        if (active.kind === 'relic') {
          setVaultPulse(true);
          later(() => setVaultPulse(false), CHEST_PULSE_MS);
        }
      }, SUCCESS_TO_CLOSE_MS);
    }, CONFIRM_TO_SUCCESS_MS);
  }, [animating, dispatch, onServerBalanceChanged, userId]);

  /** Equip an owned companion from its reveal modal, then close. */
  const equip = useCallback(
    async guildId => {
      await dispatch(equipCompanion({ userId, guildId }));
      close();
    },
    [close, dispatch, userId],
  );

  return {
    activeItem,
    stage,
    animating,
    vaultPulse,
    error,
    openRelic,
    openCompanion,
    close,
    confirm,
    equip,
  };
}
